import React from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Target } from 'lucide-react';

export interface NewWorkbookFormState {
  userGoal: string;
}

interface NewWorkbookFormProps {
  value: NewWorkbookFormState;
  onChange: (value: NewWorkbookFormState) => void;
  showValidation?: boolean;
}

export const NewWorkbookForm: React.FC<NewWorkbookFormProps> = ({
  value,
  onChange,
  showValidation = false,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      {/* Question */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-purple-600 dark:text-purple-400">
          Project Name
        </h3>
        
        <Input
          id="userGoal"
          placeholder="Launch My E-commerce Clothing Brand"
          value={value.userGoal}
          onChange={(e) => {
            const inputValue = e.target.value;
            // Limit to 40 characters
            if (inputValue.length <= 40) {
              onChange({ userGoal: inputValue });
            }
          }}
          className={cn(
            "w-full transition-colors text-base bg-white/5 border-white/10 text-white placeholder:text-gray-400",
            showValidation && value.userGoal.length < 10 
              ? "border-red-300 focus:border-red-500" 
              : "focus:border-purple-500 focus:ring-purple-500/20"
          )}
          maxLength={40}
        />
        
        {/* Character counter */}
        <div className="flex justify-between items-center text-xs">
          <div className={cn(
            "transition-colors duration-200",
            showValidation && value.userGoal.length < 10 
              ? "text-red-400" 
              : "text-purple-400"
          )}>
            {showValidation && value.userGoal.length < 10 
              ? "Please provide a more descriptive project name"
              : ""}
          </div>
          <div className="text-gray-400 font-mono">
            {value.userGoal.length}/40 characters
          </div>
        </div>
        
        {/* Minimal validation message */}
        <AnimatePresence>
          {showValidation && value.userGoal.length < 10 && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="text-sm text-red-500 flex items-center gap-2"
            >
              <Target size={14} />
              Please provide a more descriptive project name
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
